<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div><div class="page-kicker">Tenancies</div><h2>Properties</h2></div>
            <div>
                <el-button size="small" type="primary" @click="syncFromBuildium">Sync from Buildium</el-button>
            </div>
        </div>

        <section class="page-section">
            <div class="form-inline">
                <div class="form-item">
                    <div class="form-item-label">Property Name</div>
                    <div class="form-item-input">
                        <el-input size="medium" class="w400" :clearable="true" v-model="params.name"/>
                    </div>
                </div>
                <div class="form-item">
                    <el-button size="medium" type="primary" @click="onSearch">{{ $t('post.search') }}</el-button>
                </div>
            </div>
        </section>

        <section class="page-section">
            <el-table :data="dataList" @selection-change="onSelectionChange" border>
                <el-table-column width="50" type="selection"/>
                <el-table-column prop="custom_id" label="ID" width="80"/>
                <el-table-column prop="Name" label="Name" min-width="150"/>
                <el-table-column prop="formatted_address" label="Address" min-width="200"/>
                <el-table-column prop="Status" label="Status" width="120">
                    <template slot-scope="scope">
                        <el-tag :type="scope.row.IsActive ? 'success' : 'danger'">
                            {{ scope.row.IsActive ? 'Active' : 'Inactive' }}
                        </el-tag>
                    </template>
                </el-table-column>

                <!-- RentalType 只能在编辑模式下修改 -->
                <el-table-column prop="RentalType" label="Rental Type" width="150">
                    <template slot-scope="scope">
                        <div v-if="scope.row.isEditing">
                            <el-select v-model="scope.row.RentalType" placeholder="Select Rental Type" @change="onTypeChange(scope.row)">
                                <el-option label="Commercial" value="Commercial"></el-option>
                                <el-option label="Residential" value="Residential"></el-option>
                            </el-select>
                        </div>
                        <div v-else>
                            {{ scope.row.RentalType }}
                        </div>
                    </template>
                </el-table-column>

                <!-- GST Account 只能在编辑模式下修改 -->
                <el-table-column prop="GSTAccount" label="GST Account" width="200">
                    <template slot-scope="scope">
                        <div v-if="scope.row.isEditing">
                            <el-select v-model="scope.row.GSTAccount" placeholder="Select GST Account"
                                       :disabled="scope.row.RentalType !== 'Commercial'">
                                <el-option label="(Empty)" value=""></el-option>
                                <el-option label="GST Paid Out" value="GST Paid Out"></el-option>
                                <el-option label="GST_Remittance to CRA" value="GST_Remittance to CRA"></el-option>
                            </el-select>
                        </div>
                        <div v-else>
                            {{ scope.row.GSTAccount }}
                        </div>
                    </template>
                </el-table-column>

                <el-table-column prop="created_at" label="Time" width="170" align="right"/>

                <!-- 操作列 -->
                <el-table-column label="Actions" width="200">
                    <template slot-scope="scope">
                        <div v-if="!scope.row.isEditing">
                            <el-button size="mini" type="primary" @click="enableEditing(scope.row)">Edit</el-button>
                        </div>
                        <div v-else>
                            <el-button size="mini" type="success" @click="saveChanges(scope.row)">Save</el-button>
                            <el-button size="mini" @click="cancelEditing(scope.row)">Cancel</el-button>
                        </div>
                    </template>
                </el-table-column>
            </el-table>

            <div class="table-edit-footer">
                <div class="table-actions">
                    <el-button size="small" type="primary" :disabled="!selectionIds.length" @click="onBatchDelete">
                        {{ $t('common.batch_delete') }}
                    </el-button>
                </div>
                <el-pagination
                    background
                    layout="prev, pager, next, total"
                    :total="total"
                    :page-size="pageSize"
                    :current-page="page"
                    @current-change="onPageChange"
                />
            </div>
        </section>
    </main-layout>
</template>

<script>
import Pagination from "../mixins/Pagination";
import ApiService from "../utils/ApiService";

export default {
    name: "RentalProperties",
    mixins: [Pagination],
    data() {
        return {
            autoSearch: true,
            params: {
                name: '',
                RentalType: '',
                GSTAccount: ''
            },
            originalData: {}
        }
    },
    methods: {
        listApi() {
            return '/buildium/rentals/properties';
        },
        listParams() {
            return this.params;
        },
        onSearch() {
            this.fetchList();
        },
        syncFromBuildium() {
            const loading = this.$loading({
                lock: true,
                text: 'Synchronizing...',
                spinner: 'el-icon-loading',
                background: 'rgba(0, 0, 0, 0.7)'
            });
            this.$api.get('/buildium/rentals/properties/sync').then(response => {
                this.$message.success('Sync completed');
                this.fetchList();
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                loading.close();
            });
        },
        onBatchDelete() {
            let ids = this.selectionIds.map(item => item.Id);
            this.$confirm(this.$t('common.delete_tips'), this.$t('common.delete_confirm'), {
                type: 'warning'
            }).then(() => {
                ApiService.delete('/buildium/rentals/properties/batch', { data: { ids } }).then(() => {
                    this.$message.success('Deleted successfully');
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                });
            }).catch(() => {
                this.$message.info('Canceled');
            });
        },
        // 当 Type 变更时自动清空 GSTAccount
        onTypeChange(row) {
            if (row.RentalType === 'Residential') {
                row.GSTAccount = "";
            }
        },
        enableEditing(row) {
            this.$set(row, 'isEditing', true);
            this.originalData[row.Id] = { RentalType: row.RentalType, GSTAccount: row.GSTAccount };
        },
        cancelEditing(row) {
            if (this.originalData[row.Id]) {
                row.RentalType = this.originalData[row.Id].RentalType;
                row.GSTAccount = this.originalData[row.Id].GSTAccount;
            }
            this.$set(row, 'isEditing', false);
        },
        saveChanges(row) {
            ApiService.put(`/buildium/rentals/properties/${row.Id}`, { RentalType: row.RentalType, GSTAccount: row.GSTAccount })
                .then(() => {
                    this.$message.success('Updated successfully');
                    this.$set(row, 'isEditing', false);
                })
                .catch(reason => {
                    this.$message.error(reason.message || 'Update failed');
                });
        }
    },
    watch: {
        // The top-bar search links here with ?name=…
        '$route.query.name'(value) {
            this.params.name = value || '';
        },
    },
    mounted() {
        if (this.$route.query.name) {
            this.params.name = this.$route.query.name;
        }
        this.fetchList();
    }
}
</script>

<style scoped>
</style>
